
import { PencilIcon, CheckIcon, XMarkIcon } from "@heroicons/vue/24/solid";
import { PlanMixins, EventToEdit, durationService } from "@oculai/planner";
import { PlannerComment } from "@oculai/planner";
import { set } from "date-fns";
import { defineComponent, PropType } from "vue";
import "@/assets/styles/custom.css";
import { ProjectDurationSettings } from "@/types/ProjectDurationSettings";
import DateTime from "@/views/planner/components/DateTime.vue";
import PlannerComments from "@/views/planner/components/PlannerComments.vue";
import SlideOver from "@/views/planner/components/SlideOver.vue";

export default defineComponent({
  name: "EventEditor",
  props: {
    mode: {
      type: String as PropType<"hide" | "edit">,
      required: true,
    },
    eventRecord: {
      type: Object as PropType<EventToEdit>,
      required: true,
    },
    now: {
      type: Date,
      required: true,
    },
    projectDurationSettings: {
      type: Object as PropType<ProjectDurationSettings>,
      required: false,
    },
  },
  emits: ["saveEvent", "cancelEdit", "commentAdded", "commentDeleted", "nameEdited"],
  components: {
    SlideOver,
    DateTime,
    PlannerComments,
    PencilIcon,
    CheckIcon,
    XMarkIcon,
  },
  data() {
    return {
      startDate: null as Date | null,
      endDate: null as Date | null,
      isMilestone: false as boolean,
      comments: [] as PlannerComment[],
      isEditingName: false,
      editingName: "",
      workingDays: 0,
    };
  },
  mixins: [PlanMixins],
  computed: {
    eventTitleColor() {
      return this.eventRecord.type === "planned" ? "#bccfcc" : "#336260";
    },
    eventTitle() {
      const translatedEventTypeLabels: Record<string, string> = {
        actual: this.$t("analytics.planner.actual_event_name").toUpperCase(),
        planned: this.$t("analytics.planner.planned_event_name").toUpperCase(),
      };

      return translatedEventTypeLabels[this.eventRecord.type] || "";
    },
    validationErrorMessage() {
      return this.getStartEndDateValidationMessageForPlannedEvent(
        this.startDate,
        this.endDate,
        this.isMilestone,
      );
    },
    plannerCommentsKey() {
      return `${this.currentCustomerName}-${this.currentSiteId}-${this.eventRecord.sourceId}`;
    },
    durationSettings() {
      return this.projectDurationSettings
        ? durationService.calculateSettings(
            this.projectDurationSettings.working_hours,
            this.projectDurationSettings.non_working_days,
          )
        : null;
    },
    durationSettingsWithoutDisturbances() {
      return this.projectDurationSettings
        ? durationService.calculateSettings(
            this.projectDurationSettings.working_hours,
            this.projectDurationSettings.non_working_days.filter(
              (nonWorkingDay) => nonWorkingDay.type !== "disturbance",
            ),
          )
        : null;
    },
  },
  watch: {
    mode() {
      this.initialize();
    },
    startDate() {
      if (this.isMilestone) {
        this.endDate = this.startDate;
      }
    },
  },
  mounted() {
    this.initialize();
  },
  methods: {
    initialize() {
      if (this.eventRecord && this.mode !== "hide") {
        this.startDate = this.eventRecord.startDate;
        this.endDate = this.eventRecord.endDate;
        this.isMilestone =
          this.eventRecord.startDate?.getTime() === this.eventRecord.endDate?.getTime();
      }
      this.isEditingName = false;
      this.calculateWorkingDays();
    },
    saveEvent() {
      if (!this.validationErrorMessage) {
        this.$emit("saveEvent", this.startDate, this.endDate);
      }
    },
    toggleMilestone() {
      if (this.eventRecord.type === "planned") {
        this.isMilestone = !this.isMilestone;
        this.endDate = this.isMilestone ? this.startDate : set(this.now, { hours: 17, minutes: 0 });
        this.calculateWorkingDays();
      }
    },
    handleEditNameClick() {
      this.isEditingName = true;
      this.editingName = this.eventRecord.name;
      setTimeout(() => {
        (this.$refs.editingNameRef as HTMLInputElement)?.focus();
      }, 50);
    },
    handleNameEditingConfirm() {
      this.isEditingName = false;
      this.$emit("nameEdited", { eventRecord: this.eventRecord, name: this.editingName });
    },
    calculateWorkingDays() {
      const durationSettings =
        this.eventRecord.type === "planned"
          ? this.durationSettingsWithoutDisturbances
          : this.durationSettings;
      if (durationSettings && this.startDate && this.endDate) {
        this.workingDays = durationService.calculateDuration(
          durationSettings,
          this.startDate,
          this.endDate,
        ).workingDays;
      }
    },
    handleWorkingDaysInput() {
      if (this.durationSettings && this.startDate && typeof this.workingDays === "number") {
        const endDate = durationService.calculateEndDate(
          this.durationSettings,
          this.startDate,
          this.workingDays,
        );
        this.endDate = set(endDate, { hours: 17, minutes: 0 });
      }
    },
  },
});
